import { Sparkles, Trash2 } from 'lucide-react'
import { countEffectiveChars, zStringEffectiveLimit } from '@/lib/text/count'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { NARRATIVE_PRESET } from '@/features/editor/data/general-world-core'
import { EDITOR_MANUAL_URL } from '@/features/editor/lib/manual'
import { useEditorStore } from '@/features/editor/stores/editor-store'
import { SectionCard } from './section-card'

export function NarrativeSection() {
  const narrativeCore = useEditorStore((s) => s.draft.narrativeCore)
  const patchDraft = useEditorStore((s) => s.patchDraft)

  const length = countEffectiveChars(narrativeCore)
  const limit = 2000

  // 严格匹配判断是否使用了模板
  const isUsingPreset = narrativeCore === NARRATIVE_PRESET

  const handleTogglePreset = () => {
    if (isUsingPreset) {
      // 清空（允许重写）
      patchDraft({ narrativeCore: '' })
    } else {
      // 填入模板
      patchDraft({ narrativeCore: NARRATIVE_PRESET })
    }
  }

  return (
    <SectionCard
      title='叙事核心'
      description={
        <>
          这里决定“讲什么”，是游戏的“引力中心”，它确保 AI
          的即兴发挥始终围绕核心主题， 如有疑惑，请参考
          <a
            href={EDITOR_MANUAL_URL}
            target='_blank'
            rel='noreferrer'
            className='text-primary underline-offset-4 hover:underline'
          >
            “使用详解”
          </a>
          。
          <span className='text-muted-foreground block'>
            请勿填写完整的故事大纲，您只需给予AI叙事的方向，用高密度信息达成最优的效果。
          </span>
        </>
      }
    >
      <div className='space-y-2'>
        {isUsingPreset ? (
          <div className='border-input bg-muted/50 text-muted-foreground flex min-h-[200px] cursor-not-allowed flex-col items-center justify-center gap-2 rounded-md border select-none'>
            <Sparkles className='h-5 w-5 opacity-50' />
            <span className='text-sm font-medium'>已填入预制化叙事核心</span>
          </div>
        ) : (
          <Textarea
            value={narrativeCore}
            onChange={(e) => patchDraft({ narrativeCore: e.target.value })}
            className={cn(
              'min-h-[200px] font-mono text-xs leading-relaxed',
              !zStringEffectiveLimit(limit).safeParse(narrativeCore).success &&
                'border-destructive focus-visible:ring-destructive/30'
            )}
            placeholder={`{{user}} 是谁？在什么时间/背景下被卷入了什么事件？{{user}} 当前的处境是？
{{user}} 拥有的王牌/特殊能力/资源是？
{{user}} 的最高优先级任务是什么？为了实现这一点，{{user}} 必须采取具体手段/行动逻辑是？
{{user}} 是否需要在时间限制/特定条件下，处理具体的矛盾点，防止最坏的结局发生？`}
          />
        )}

        <div className='flex items-center justify-between gap-2'>
          <Button
            variant='ghost'
            size='sm'
            onClick={handleTogglePreset}
            className={cn(
              'h-7 px-2 text-xs',
              isUsingPreset
                ? 'text-destructive hover:text-destructive hover:bg-destructive/10'
                : 'text-primary hover:text-primary hover:bg-primary/10'
            )}
          >
            {isUsingPreset ? (
              <>
                <Trash2 className='mr-1.5 h-3.5 w-3.5' />
                清除(解锁编辑)
              </>
            ) : (
              <>
                <Sparkles className='mr-1.5 h-3.5 w-3.5' />
                世界模拟(预制叙事核心)
              </>
            )}
          </Button>

          <div className='text-muted-foreground text-xs'>
            {isUsingPreset ? (
              '已锁定'
            ) : length > limit ? (
              <span className='text-destructive font-bold'>
                字符：{length}/{limit} (超限)
              </span>
            ) : (
              `字符：${length}/${limit}`
            )}
          </div>
        </div>
      </div>
    </SectionCard>
  )
}
